import React, { useState } from 'react';
import { AiOutlineEdit, AiOutlinePlus } from 'react-icons/ai';
import Header from '../components/Header';
import Modal from '../components/Modal';
import DataGrid from '../components/DataGrid';
import Pagination from '../components/Pagination';
import { ActionButton } from '../components/Button';
import { useCategories } from '../hooks/useCategories';
import { usePatch } from '../hooks/usePatch';
import { usePost } from '../hooks/usePost';
import { useDelete } from '../hooks/useDelete';

const Categories = () => {
    const { categories: { parents, children }, refresh } = useCategories();
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState('edit');
    const [formData, setFormData] = useState({
        title: '',
        severity: 'MODERATE',
        order: 1,
        icon: '',
        parentCategoryId: ''
    });
    const [subCategories, setSubCategories] = useState([]);
    const { loading: patchLoading, putRequest } = usePatch()
    const { loading: postLoading, postRequest } = usePost()
    const { loading: deleteLoading, deleteRequest } = useDelete();

    const handleEditCategory = (category) => {
        setSelectedCategory(category);
        setFormData({
            title: category.title,
            severity: category.severity,
            order: category.order,
            icon: category.icon,
            parentCategoryId: category.id
        });
        setSubCategories(children.filter(child => child.parentCategoryId === category.id));
        setModalType('edit');
        setIsModalOpen(true);
    };


    const handleSaveCategory = () => {
        if (modalType === 'edit') {
            Promise.all([
                putRequest(`/categories/${selectedCategory.id}`, {
                    title: formData.title,
                    severity: formData.severity,
                    order: formData.order,
                    icon: formData.icon
                }),
                ...subCategories.map(subCat => {
                    if (subCat.id.startsWith('subcat_')) {
                        return postRequest(`/categories`, {
                            title: subCat.title,
                            severity: subCat.severity,
                            order: subCat.order,
                            icon: subCat.icon,
                            parentCategory: selectedCategory.id
                        });
                    } else {
                        return putRequest(`/categories/${subCat.id}`, {
                            title: subCat.title,
                            severity: subCat.severity,
                            order: subCat.order,
                            icon: subCat.icon,
                            parentCategory: selectedCategory.id
                        });
                    }
                })
            ]).then(() => {
                refresh()
            });
        }
        setIsModalOpen(false);
    };

    const handleCreateSubCategory = () => {
        const newSubCategory = {
            id: `subcat_${Date.now()}`,
            title: '',
            severity: 'MODERATE',
            order: subCategories.length + 1,
            icon: '',
            parentCategoryId: selectedCategory.id,
        };
        setSubCategories([...subCategories, newSubCategory]);
    };

    const handleSubCategoryChange = (index, field, value) => {
        const updatedSubCategories = [...subCategories];
        updatedSubCategories[index] = {
            ...updatedSubCategories[index],
            [field]: value
        };
        setSubCategories(updatedSubCategories);
    };

    const handleDeleteSubCategory = async (index) => {
        const isConfirm = window.confirm('Êtes-vous sûr de vouloir supprimer cette sous-catégorie ? Cette action est irréversible.');
        if (isConfirm) {
            await deleteRequest(`/categories/${subCategories[index].id}`).then(() => {
                const updatedSubCategories = subCategories.filter((_, i) => i !== index);
                setSubCategories(updatedSubCategories);
                refresh();
            })
        }

    };

    const getSeverityBadge = (severity) => {
        const severityMap = {
            'MODERATE': { bg: 'bg-green-100', text: 'text-green-800', label: 'Modérée' },
            'HIGH': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Élevée' },
            'CRITICAL': { bg: 'bg-red-100', text: 'text-red-800', label: 'Critique' }
        };
        const config = severityMap[severity] || severityMap['MODERATE'];
        return (
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}>
                {config.label}
            </span>
        );
    };

    const getSubCategoriesTemplate = (props) => {
        const subCatCount = children.filter(child => child.parentCategoryId === props.id).length;
        return (
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                {subCatCount}
            </span>
        );
    }

    const SeverityTemplate = (props) => getSeverityBadge(props.severity);
    const suCategoriesTemplate = (props) => getSubCategoriesTemplate(props);


    const CategoryActionTemplate = (props) => (
        <div className="flex gap-2 justify-center">
            <ActionButton
                onClick={() => handleEditCategory(props)}
                icon={AiOutlineEdit}
                className='text-blue-600 border-blue-500 hover:bg-blue-500 hover:text-white'
            />
        </div>
    );


    const categoriesColumns = [
        { field: 'title', headerText: 'Catégorie Parent', width: 200, textAlign: 'Center' },
        {
            headerText: 'Sévérité',
            width: 150,
            textAlign: 'Center',
            template: SeverityTemplate,
            allowEditing: false,
        },
        {
            headerText: 'Sous-catégories',
            width: 100,
            textAlign: 'Center',
            template: suCategoriesTemplate,
            allowEditing: false,
        },
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const totalItems = parents.length;

    return (
        <div className='md:m-10 mt-auto p-2 pb-4 md:p-10 bg-gray-200 md:rounded-3xl rounded-xl w-100'>
            <Header
                category='Catégories'
                title=''
                refresh={() => refresh()}
            />

            <DataGrid
                data={parents} // Changed from children to parents
                columns={categoriesColumns}
                allowPaging={false}
                loading={deleteLoading || postLoading || patchLoading}
                editing={{ allowDeleting: false, allowEditing: false }}
                toolbarOptions={[]}
                actionButtons={CategoryActionTemplate}
                extraComponents={
                    <Pagination
                        currentPage={1}
                        totalPages={1}
                        totalItems={totalItems}
                        onPageChange={(val) => handleParamsChange('page', val)}
                    />
                }
            />

            {isModalOpen && (
                <Modal
                    show={isModalOpen}
                    title={`Gérer la catégorie: ${selectedCategory?.title}`}
                    onClose={() => setIsModalOpen(false)}
                    size="lg"
                >
                    <div className="space-y-6">
                        {/* Parent Category Info */}
                        <div className="border-b pb-4">
                            <h3 className="text-lg font-medium text-gray-700 dark:text-white mb-3">
                                Informations de la catégorie parent
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">
                                        Titre
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">
                                        Sévérité
                                    </label>
                                    <select
                                        name="severity"
                                        required
                                        value={formData.severity}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="LOW">Faible</option>
                                        <option value="MODERATE">Modérée</option>
                                        <option value="HIGH">Élevée</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Subcategories Management */}
                        <div>
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-lg font-medium text-gray-700 dark:text-white">
                                    Sous-catégories
                                </h3>
                                <button
                                    type="button"
                                    onClick={handleCreateSubCategory}
                                    className="flex items-center gap-2 px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors text-sm"
                                >
                                    <AiOutlinePlus />
                                    Ajouter sous-catégorie
                                </button>
                            </div>

                            <div className="space-y-3 max-h-60 overflow-y-auto">
                                {subCategories.map((subCat, index) => (
                                    <div key={subCat.id} className="flex gap-2 items-center p-3 border border-gray-200 rounded-md bg-gray-50">
                                        <div className="flex-1">
                                            <input
                                                type="text"
                                                placeholder="Titre de la sous-catégorie"
                                                value={subCat.title}
                                                onChange={(e) => handleSubCategoryChange(index, 'title', e.target.value)}
                                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                        <div className="w-32">
                                            <select
                                                value={subCat.severity}
                                                onChange={(e) => handleSubCategoryChange(index, 'severity', e.target.value)}
                                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                            >
                                                <option value="MODERATE">Modérée</option>
                                                <option value="HIGH">Élevée</option>
                                                <option value="CRITICAL">Critique</option>
                                            </select>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteSubCategory(index)}
                                            className="px-2 py-1 text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors text-sm"
                                        >
                                            Supprimer
                                        </button>
                                    </div>
                                ))}
                                {subCategories.length === 0 && (
                                    <div className="text-center py-6 text-gray-500">
                                        Aucune sous-catégorie. Cliquez sur "Ajouter sous-catégorie" pour en créer une.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                        <button
                            type="button"
                            onClick={() => setIsModalOpen(false)}
                            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            onClick={handleSaveCategory}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                        >
                            Sauvegarder
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default Categories;